import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Form,
  Button,
  Spinner,
  Alert,
  Badge,
  Modal,
  Tabs,
  Tab,
} from "react-bootstrap";
import Select from "react-select";
import { useAuth } from "../providers/AuthContext";
import { ProjectContext } from "../providers/ProjectContext";
import {
  fetchTimeSyncBreakdown,
  fetchTimeSyncPeriodBreakdown,
  fetchTimeSyncLocks,
  createTimeSyncLock,
  unlockTimeSyncLock,
  runTimeSync,
  runTimeSyncBatch,
} from "../services/apiService";

const fmtDate = (d) => d.toISOString().split("T")[0];

// Monday-based start of the week containing d.
const startOfWeek = (d) => {
  const x = new Date(d);
  const offset = (x.getDay() + 6) % 7; // 0 = Monday
  x.setDate(x.getDate() - offset);
  return x;
};

const fmtDuration = (seconds) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return `${hrs}h ${mins}m`;
};

// Build the preset periods, each resolving to a concrete { start, end } range.
// "Since last sync" only appears for projects that have been synced before;
// "All time" (project history, no dates) only when allowed.
const buildPeriods = (project, { withAllTime = false } = {}) => {
  const today = new Date();
  const periods = [];

  if (project?.lastTimeSyncDate) {
    const last = new Date(project.lastTimeSyncDate);
    if (!isNaN(last.getTime())) {
      periods.push({
        key: "since",
        label: "Since last sync",
        start: fmtDate(last),
        end: fmtDate(today),
      });
    }
  }

  const thisWeekStart = startOfWeek(today);
  periods.push({
    key: "thisWeek",
    label: "This week",
    start: fmtDate(thisWeekStart),
    end: fmtDate(today),
  });

  const lastWeekStart = startOfWeek(today);
  lastWeekStart.setDate(lastWeekStart.getDate() - 7);
  const lastWeekEnd = new Date(lastWeekStart);
  lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
  periods.push({
    key: "lastWeek",
    label: "Last week",
    start: fmtDate(lastWeekStart),
    end: fmtDate(lastWeekEnd),
  });

  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  periods.push({
    key: "thisMonth",
    label: "This month",
    start: fmtDate(thisMonthStart),
    end: fmtDate(today),
  });

  const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
  periods.push({
    key: "lastMonth",
    label: "Last month",
    start: fmtDate(lastMonthStart),
    end: fmtDate(lastMonthEnd),
  });

  if (withAllTime) {
    periods.push({ key: "allTime", label: "All time", start: "", end: "" });
  }

  return periods;
};

const prettyRange = (start, end) => {
  if (!start || !end) return "";
  const opts = { day: "2-digit", month: "short", year: "numeric" };
  const s = new Date(`${start}T00:00:00`).toLocaleDateString(undefined, opts);
  const e = new Date(`${end}T00:00:00`).toLocaleDateString(undefined, opts);
  return s === e ? s : `${s} – ${e}`;
};

// ---------------------------------------------------------------------------
// Shared building blocks
// ---------------------------------------------------------------------------

// Period preset buttons + custom date inputs. Controlled via periodKey and the
// custom dates; reports the resolved range through the derived values the
// parent computes (see usePeriod).
function PeriodButtons({
  periods,
  periodKey,
  setPeriodKey,
  customStart,
  setCustomStart,
  customEnd,
  setCustomEnd,
  disabled = false,
}) {
  return (
    <>
      <div className="flex flex-wrap gap-2">
        {periods.map((p) => (
          <Button
            key={p.key}
            size="sm"
            variant={periodKey === p.key ? "primary" : "outline-secondary"}
            onClick={() => setPeriodKey(p.key)}
            disabled={disabled}
          >
            {p.label}
          </Button>
        ))}
        <Button
          size="sm"
          variant={periodKey === "custom" ? "primary" : "outline-secondary"}
          onClick={() => setPeriodKey("custom")}
          disabled={disabled}
        >
          Custom…
        </Button>
      </div>

      {periodKey === "custom" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
          <div>
            <Form.Label className="text-sm text-muted">Start date</Form.Label>
            <Form.Control
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
            />
          </div>
          <div>
            <Form.Label className="text-sm text-muted">End date</Form.Label>
            <Form.Control
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
            />
          </div>
        </div>
      )}
    </>
  );
}

// Checkbox list of people with per-target badges. Expects normalized rows:
// { togglUserId, name, entryCount, formattedDuration, mapped, sonderOk, xeroOk }
// where sonderOk/xeroOk mean "this person can reach that target here".
function PeopleChecklist({
  people,
  selectedIds,
  onTogglePerson,
  onToggleAll,
  syncToSonder,
  syncToXero,
}) {
  const canSync = (p) => p.sonderOk || p.xeroOk;
  const selectableIds = people.filter(canSync).map((p) => p.togglUserId);
  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));

  return (
    <>
      <div className="flex items-center justify-between mt-2 mb-2">
        <button
          type="button"
          className="text-sm text-blue-600 hover:underline bg-transparent border-0 p-0"
          onClick={() => onToggleAll(allSelected ? [] : selectableIds)}
          disabled={selectableIds.length === 0}
        >
          {allSelected ? "Deselect all" : "Select all syncable"}
        </button>
        <span className="text-sm text-gray-500">
          {selectableIds.length} of {people.length} syncable
        </span>
      </div>

      <div className="border rounded divide-y">
        {people.map((p) => {
          const usable = canSync(p);
          return (
            <label
              key={p.togglUserId}
              className={`flex items-center gap-3 px-3 py-2 mb-0 ${
                usable ? "cursor-pointer" : "opacity-60"
              }`}
            >
              <input
                type="checkbox"
                checked={usable && selectedIds.has(p.togglUserId)}
                disabled={!usable}
                onChange={() => onTogglePerson(p.togglUserId)}
              />
              <span className="flex-1">
                <span className="font-medium">{p.name}</span>
                <span className="text-gray-500 text-sm">
                  {" "}
                  · {p.entryCount} {p.entryCount === 1 ? "entry" : "entries"}
                </span>
              </span>
              <span className="text-sm font-mono text-gray-700">
                {p.formattedDuration}
              </span>
              <span className="w-28 text-right">
                {usable ? (
                  <>
                    {p.sonderOk && (
                      <Badge
                        bg={syncToSonder ? "primary" : "light"}
                        text={syncToSonder ? undefined : "dark"}
                        className="me-1"
                      >
                        Sonder
                      </Badge>
                    )}
                    {p.xeroOk && (
                      <Badge
                        bg={syncToXero ? "primary" : "light"}
                        text={syncToXero ? undefined : "dark"}
                      >
                        Xero
                      </Badge>
                    )}
                  </>
                ) : (
                  <Badge bg="light" text="dark">
                    {p.mapped ? "no target id" : "not mapped"}
                  </Badge>
                )}
              </span>
            </label>
          );
        })}
      </div>
    </>
  );
}

// Type-CONFIRM gate before any real write. summary is JSX rendered above the
// input (what's about to be created, for which scope).
function ConfirmSyncModal({
  show,
  onHide,
  onConfirm,
  syncing,
  error,
  targetLabels,
  summary,
}) {
  const [confirmText, setConfirmText] = useState("");
  useEffect(() => {
    if (show) setConfirmText("");
  }, [show]);
  const confirmOk = confirmText.trim() === "CONFIRM";

  return (
    <Modal show={show} onHide={() => !syncing && onHide()} centered>
      <Modal.Header closeButton={!syncing}>
        <Modal.Title>Confirm live sync</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-2">
          This creates <strong>real time entries</strong> in{" "}
          <strong>{targetLabels || "the selected targets"}</strong> for:
        </p>
        {summary}
        <Form.Label className="text-sm">
          Type <strong>CONFIRM</strong> to proceed:
        </Form.Label>
        <Form.Control
          autoFocus
          value={confirmText}
          placeholder="CONFIRM"
          disabled={syncing}
          onChange={(e) => setConfirmText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && confirmOk) onConfirm();
          }}
        />
        {error && (
          <Alert variant="danger" className="mt-3 mb-0">
            {error}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={syncing}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={onConfirm}
          disabled={!confirmOk || syncing}
        >
          {syncing ? (
            <>
              <Spinner size="sm" animation="border" className="me-2" />
              Syncing…
            </>
          ) : (
            "Run sync"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

const lockRangeLabel = (lock) =>
  `${lock.startDate || "the beginning"} – ${lock.endDate || "ongoing"}`;

// Shown when a loaded range overlaps a locked period — the sync button is
// disabled alongside this, and the server refuses the sync regardless.
function LockedBanner({ locked }) {
  if (!locked) return null;
  return (
    <Alert variant="danger" className="mt-3 mb-0">
      <strong>Locked period.</strong> This range overlaps the locked period{" "}
      <strong>{lockRangeLabel(locked)}</strong>
      {locked.reason && <> — {locked.reason}</>}. Syncing is blocked until an
      admin unlocks it in the <em>Locked periods</em> tab.
    </Alert>
  );
}

// Per-target summary card.
function TargetSummary({ label, data }) {
  if (!data) return null;
  return (
    <div className="border rounded p-3">
      <div className="font-semibold mb-1">{label}</div>
      <div className="text-sm">
        <span className="text-green-700">{data.created} created</span> ·{" "}
        <span className="text-gray-600">{data.skipped} skipped</span> ·{" "}
        <span className={data.errors?.length ? "text-red-600" : "text-gray-600"}>
          {data.errors?.length || 0} errors
        </span>
      </div>
      {data.errors?.length > 0 && (
        <ul className="text-xs text-red-600 mt-2 list-disc pl-4">
          {data.errors.slice(0, 5).map((e, i) => (
            <li key={i}>
              #{e.togglEntryId}: {e.error}
            </li>
          ))}
          {data.errors.length > 5 && <li>…and {data.errors.length - 5} more</li>}
        </ul>
      )}
    </div>
  );
}

function ResultPanel({ result }) {
  return (
    <div className="mt-4 bg-white shadow-md rounded p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-bold">Results</h3>
        <Badge bg="success">Live</Badge>
      </div>

      <p className="text-sm text-gray-700 mb-3">
        Project <span className="font-semibold">{result.project}</span> ·{" "}
        {result.togglEntriesFound} Toggl entries found
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {result.targets?.sonder && (
          <TargetSummary label="Sonderplan" data={result.sonder} />
        )}
        {result.targets?.xero && (
          <TargetSummary label="Xero" data={result.xero} />
        )}
      </div>
      {result.xeroClosed && (
        <p className="text-sm text-amber-600 mt-3 mb-0">
          Xero was skipped — the linked Xero project is closed.
        </p>
      )}
      {!result.targets?.sonder && !result.targets?.xero && (
        <Alert variant="info" className="mb-0">
          No targets ran for this project — check that it has the required ids
          (Sonder id, Xero id/task) and that the targets are enabled.
        </Alert>
      )}
    </div>
  );
}

// Batch results: totals up top, then a compact per-project row list.
function BatchResultPanel({ result }) {
  return (
    <div className="mt-4 bg-white shadow-md rounded p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-bold">Results</h3>
        <Badge bg="success">Live</Badge>
      </div>

      <p className="text-sm text-gray-700 mb-3">
        {result.projectsProcessed} of {result.totalProjects} projects processed
        · <span className="text-green-700">{result.totalSonderCreated}</span>{" "}
        Sonder + <span className="text-green-700">{result.totalXeroCreated}</span>{" "}
        Xero entries created
        {result.projectsWithErrors > 0 && (
          <>
            {" "}
            ·{" "}
            <span className="text-red-600">
              {result.projectsWithErrors}{" "}
              {result.projectsWithErrors === 1 ? "project" : "projects"} failed
            </span>
          </>
        )}
      </p>

      <div className="border rounded divide-y">
        {(result.results || []).map((r, i) => {
          const errCount =
            (r.sonder?.errors?.length || 0) + (r.xero?.errors?.length || 0);
          return (
            <div key={i} className="px-3 py-2">
              <div className="flex items-center gap-3">
                <span className="font-medium flex-1">{r.project}</span>
                {r.error ? (
                  <span className="text-sm text-red-600">{r.error}</span>
                ) : (
                  <span className="text-sm text-gray-600">
                    Sonder {r.sonder?.created ?? 0} created /{" "}
                    {r.sonder?.skipped ?? 0} skipped · Xero{" "}
                    {r.xero?.created ?? 0} created / {r.xero?.skipped ?? 0}{" "}
                    skipped
                    {r.xeroClosed && (
                      <span className="text-amber-600"> · Xero closed</span>
                    )}
                    {errCount > 0 && (
                      <span className="text-red-600"> · {errCount} errors</span>
                    )}
                  </span>
                )}
              </div>
              {errCount > 0 && (
                <ul className="text-xs text-red-600 mt-1 mb-0 list-disc pl-4">
                  {[...(r.sonder?.errors || []), ...(r.xero?.errors || [])]
                    .slice(0, 3)
                    .map((e, j) => (
                      <li key={j}>
                        #{e.togglEntryId}: {e.error}
                      </li>
                    ))}
                  {errCount > 3 && <li>…and {errCount - 3} more</li>}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 1 — By period: period → projects worked on → people → sync
// ---------------------------------------------------------------------------

function ByPeriodTab() {
  const today = new Date();
  const weekAgo = new Date();
  weekAgo.setDate(today.getDate() - 7);

  const [periodKey, setPeriodKey] = useState("thisWeek");
  const [customStart, setCustomStart] = useState(fmtDate(weekAgo));
  const [customEnd, setCustomEnd] = useState(fmtDate(today));

  const [breakdown, setBreakdown] = useState(null);
  const [selectedProjectIds, setSelectedProjectIds] = useState(() => new Set());
  const [projectFilter, setProjectFilter] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState(() => new Set());
  const [syncToSonder, setSyncToSonder] = useState(true);
  const [syncToXero, setSyncToXero] = useState(true);

  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const periods = useMemo(() => buildPeriods(null), []);
  const activePeriod = periods.find((p) => p.key === periodKey);
  const startDate = periodKey === "custom" ? customStart : activePeriod?.start || "";
  const endDate = periodKey === "custom" ? customEnd : activePeriod?.end || "";

  // Any change to the range invalidates a previously loaded preview so a sync
  // can never run against stale data.
  useEffect(() => {
    setBreakdown(null);
    setSyncResult(null);
    setError(null);
  }, [startDate, endDate]);

  const projects = useMemo(() => breakdown?.projects || [], [breakdown]);
  const projectSyncable = (p) => p.targets.sonder || p.targets.xero;

  const filteredProjects = useMemo(() => {
    const q = projectFilter.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) =>
      `${p.code} ${p.name}`.toLowerCase().includes(q),
    );
  }, [projects, projectFilter]);

  const selectedProjects = useMemo(
    () => projects.filter((p) => selectedProjectIds.has(p.projectId)),
    [projects, selectedProjectIds],
  );

  // Aggregate people across the selected projects. A person's target badge is
  // shown when they're mapped AND at least one selected project reaches that
  // target (per-project gaps are still skipped server-side, per entry).
  const people = useMemo(() => {
    const anySonder = selectedProjects.some((p) => p.targets.sonder);
    const anyXero = selectedProjects.some((p) => p.targets.xero);
    const byUser = new Map();
    for (const proj of selectedProjects) {
      for (const per of proj.people) {
        const cur = byUser.get(per.togglUserId) || {
          ...per,
          entryCount: 0,
          totalSeconds: 0,
        };
        cur.entryCount += per.entryCount;
        cur.totalSeconds += per.totalSeconds;
        byUser.set(per.togglUserId, cur);
      }
    }
    return [...byUser.values()]
      .map((p) => ({
        ...p,
        formattedDuration: fmtDuration(p.totalSeconds),
        sonderOk: anySonder && p.sonderMapped,
        xeroOk: anyXero && p.xeroMapped,
      }))
      .sort((a, b) => b.totalSeconds - a.totalSeconds);
  }, [selectedProjects]);

  const anySonder = selectedProjects.some((p) => p.targets.sonder);
  const anyXero = selectedProjects.some((p) => p.targets.xero);

  const personWillSync = (p) =>
    selectedUserIds.has(p.togglUserId) &&
    ((syncToSonder && p.sonderOk) || (syncToXero && p.xeroOk));
  const effectivePeople = people.filter(personWillSync);
  const effectiveSeconds = effectivePeople.reduce(
    (s, p) => s + p.totalSeconds,
    0,
  );

  const filteredSyncableIds = filteredProjects
    .filter(projectSyncable)
    .map((p) => p.projectId);
  const allFilteredSelected =
    filteredSyncableIds.length > 0 &&
    filteredSyncableIds.every((id) => selectedProjectIds.has(id));

  const toggleProject = (id) =>
    setSelectedProjectIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAllProjects = () =>
    setSelectedProjectIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredSyncableIds.forEach((id) => next.delete(id));
      } else {
        filteredSyncableIds.forEach((id) => next.add(id));
      }
      return next;
    });

  const togglePerson = (id) =>
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleLoad = async () => {
    setError(null);
    setSyncResult(null);
    if (!startDate || !endDate) {
      setError("Choose a period.");
      return;
    }
    setLoading(true);
    try {
      const data = await fetchTimeSyncPeriodBreakdown({ startDate, endDate });
      setBreakdown(data);
      // Pre-select every syncable, non-archived project…
      const preselect = (data.projects || []).filter(
        (p) => (p.targets.sonder || p.targets.xero) && !p.archived,
      );
      setSelectedProjectIds(new Set(preselect.map((p) => p.projectId)));
      // …and every person who can reach a target from those projects.
      const anyS = preselect.some((p) => p.targets.sonder);
      const anyX = preselect.some((p) => p.targets.xero);
      const ids = new Set();
      for (const proj of preselect) {
        for (const per of proj.people) {
          if ((anyS && per.sonderMapped) || (anyX && per.xeroMapped)) {
            ids.add(per.togglUserId);
          }
        }
      }
      setSelectedUserIds(ids);
      setProjectFilter("");
    } catch (err) {
      setError(
        err?.response?.data?.message || err.message || "Failed to load time.",
      );
      setBreakdown(null);
    } finally {
      setLoading(false);
    }
  };

  const locked = breakdown?.locked || null;

  const openConfirm = () => {
    setError(null);
    if (locked) {
      setError(
        `This period is locked (${lockRangeLabel(locked)}) — an admin must unlock it first.`,
      );
      return;
    }
    if (!selectedProjects.length) {
      setError("No projects selected.");
      return;
    }
    if (!effectivePeople.length) {
      setError("No syncable people selected.");
      return;
    }
    if (!syncToSonder && !syncToXero) {
      setError("Select at least one target (Sonderplan or Xero).");
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirmSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      const data = await runTimeSyncBatch({
        projectIds: selectedProjects.map((p) => p.projectId),
        startDate,
        endDate,
        syncToSonder,
        syncToXero,
        togglUserIds: effectivePeople.map((p) => p.togglUserId),
      });
      setSyncResult(data);
      setShowConfirm(false);
      // The previewed entries are now synced — clear the stale preview.
      setBreakdown(null);
      setSelectedProjectIds(new Set());
      setSelectedUserIds(new Set());
    } catch (err) {
      setError(
        err?.response?.data?.message || err.message || "Time sync failed.",
      );
    } finally {
      setSyncing(false);
    }
  };

  const targetLabels = [syncToSonder && "Sonderplan", syncToXero && "Xero"]
    .filter(Boolean)
    .join(" and ");

  const projectCodes = selectedProjects.map((p) => p.code);

  return (
    <>
      <div className="bg-white shadow-md rounded p-5">
        {/* Step 1 — Period */}
        <Form.Group className="mb-4">
          <Form.Label className="font-semibold">
            <span className="text-gray-400 mr-2">1</span>Period
          </Form.Label>
          <PeriodButtons
            periods={periods}
            periodKey={periodKey}
            setPeriodKey={setPeriodKey}
            customStart={customStart}
            setCustomStart={setCustomStart}
            customEnd={customEnd}
            setCustomEnd={setCustomEnd}
          />
          {startDate && endDate && (
            <Form.Text className="text-muted d-block mt-2">
              Pulling {prettyRange(startDate, endDate)} across all projects
            </Form.Text>
          )}
        </Form.Group>

        <Button
          variant="primary"
          onClick={handleLoad}
          disabled={loading || !startDate || !endDate}
        >
          {loading ? (
            <>
              <Spinner size="sm" animation="border" className="me-2" />
              Loading…
            </>
          ) : breakdown ? (
            "Reload time"
          ) : (
            "Load time"
          )}
        </Button>
      </div>

      {error && !showConfirm && (
        <Alert variant="danger" className="mt-4">
          {error}
        </Alert>
      )}

      {/* Step 2 — Projects worked on in the period */}
      {breakdown && (
        <div className="mt-4 bg-white shadow-md rounded p-5">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-semibold mb-0">
              <span className="text-gray-400 mr-2">2</span>Projects worked on
            </h3>
            <span className="text-sm text-gray-600">
              {breakdown.entryCount} entries · {breakdown.formattedDuration}{" "}
              total
            </span>
          </div>

          <LockedBanner locked={locked} />

          {projects.length === 0 ? (
            <Alert variant="info" className="mt-3 mb-0">
              No Tracer-tracked projects had time in{" "}
              {prettyRange(startDate, endDate)}.
            </Alert>
          ) : (
            <>
              <Form.Control
                type="search"
                className="mt-2"
                placeholder="Filter projects…"
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
              />

              <div className="flex items-center justify-between mt-2 mb-2">
                <button
                  type="button"
                  className="text-sm text-blue-600 hover:underline bg-transparent border-0 p-0"
                  onClick={toggleAllProjects}
                  disabled={filteredSyncableIds.length === 0}
                >
                  {allFilteredSelected ? "Deselect all" : "Select all"}
                  {projectFilter.trim() ? " (filtered)" : ""}
                </button>
                <span className="text-sm text-gray-500">
                  {selectedProjects.length} of {projects.length} selected
                </span>
              </div>

              <div className="border rounded divide-y max-h-96 overflow-y-auto">
                {filteredProjects.map((p) => {
                  const usable = projectSyncable(p);
                  return (
                    <label
                      key={p.projectId}
                      className={`flex items-center gap-3 px-3 py-2 mb-0 ${
                        usable ? "cursor-pointer" : "opacity-60"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={usable && selectedProjectIds.has(p.projectId)}
                        disabled={!usable}
                        onChange={() => toggleProject(p.projectId)}
                      />
                      <span className="flex-1">
                        <span className="font-medium">
                          {p.code} — {p.name}
                        </span>
                        {p.archived && (
                          <Badge bg="secondary" className="ms-2">
                            archived
                          </Badge>
                        )}
                        <span className="text-gray-500 text-sm">
                          {" "}
                          · {p.people.length}{" "}
                          {p.people.length === 1 ? "person" : "people"}
                        </span>
                      </span>
                      <span className="text-sm font-mono text-gray-700">
                        {p.formattedDuration}
                      </span>
                      <span className="w-36 text-right">
                        {usable ? (
                          <>
                            {p.targets.sonder && (
                              <Badge bg="primary" className="me-1">
                                Sonder
                              </Badge>
                            )}
                            {p.targets.xero && <Badge bg="primary">Xero</Badge>}
                            {p.xeroClosed && (
                              <Badge bg="warning" text="dark">
                                Xero closed
                              </Badge>
                            )}
                          </>
                        ) : p.xeroClosed ? (
                          <Badge bg="warning" text="dark">
                            Xero closed
                          </Badge>
                        ) : (
                          <Badge bg="light" text="dark">
                            no targets
                          </Badge>
                        )}
                      </span>
                    </label>
                  );
                })}
                {filteredProjects.length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    No projects match “{projectFilter}”.
                  </div>
                )}
              </div>

              {breakdown.unmatched?.length > 0 && (
                <details className="mt-3">
                  <summary className="text-sm text-gray-600 cursor-pointer">
                    {breakdown.unmatched.length} Toggl{" "}
                    {breakdown.unmatched.length === 1 ? "project" : "projects"}{" "}
                    with time not tracked in Tracer
                  </summary>
                  <ul className="text-sm text-gray-600 mt-2 mb-0 list-disc pl-5">
                    {breakdown.unmatched.map((u, i) => (
                      <li key={i}>
                        {u.name} · {u.formattedDuration}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </>
          )}
        </div>
      )}

      {/* Step 3 — People across the selected projects */}
      {breakdown && projects.length > 0 && (
        <div className="mt-4 bg-white shadow-md rounded p-5">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-semibold mb-0">
              <span className="text-gray-400 mr-2">3</span>People who tracked
              time
            </h3>
            <span className="text-sm text-gray-600">
              across {selectedProjects.length} selected{" "}
              {selectedProjects.length === 1 ? "project" : "projects"}
            </span>
          </div>

          {selectedProjects.length === 0 ? (
            <Alert variant="info" className="mt-3 mb-0">
              Select at least one project above.
            </Alert>
          ) : people.length === 0 ? (
            <Alert variant="info" className="mt-3 mb-0">
              No time tracked on the selected projects.
            </Alert>
          ) : (
            <>
              <PeopleChecklist
                people={people}
                selectedIds={selectedUserIds}
                onTogglePerson={togglePerson}
                onToggleAll={(ids) => setSelectedUserIds(new Set(ids))}
                syncToSonder={syncToSonder}
                syncToXero={syncToXero}
              />

              <hr className="my-4" />

              <div className="flex flex-wrap gap-6 items-center">
                <Form.Check
                  type="checkbox"
                  id="period-sync-sonder"
                  label="Sonderplan"
                  checked={syncToSonder && anySonder}
                  disabled={!anySonder}
                  onChange={(e) => setSyncToSonder(e.target.checked)}
                />
                <Form.Check
                  type="checkbox"
                  id="period-sync-xero"
                  label="Xero"
                  checked={syncToXero && anyXero}
                  disabled={!anyXero}
                  onChange={(e) => setSyncToXero(e.target.checked)}
                />
                {(!anySonder || !anyXero) && (
                  <span className="text-sm text-muted">
                    {!anySonder && "No selected project reaches Sonder"}
                    {!anySonder && !anyXero && " · "}
                    {!anyXero && "No selected project reaches Xero"}.
                  </span>
                )}
              </div>

              {!locked && (
                <Alert variant="warning" className="mt-4 mb-3">
                  <strong>Live sync.</strong> Running this creates real entries
                  in {targetLabels || "the selected targets"} — you’ll confirm
                  before anything is written.
                </Alert>
              )}
              <div className={locked ? "mt-4" : ""}>
                <Button variant="danger" onClick={openConfirm} disabled={!!locked}>
                  Sync {selectedProjects.length}{" "}
                  {selectedProjects.length === 1 ? "project" : "projects"} ·{" "}
                  {effectivePeople.length}{" "}
                  {effectivePeople.length === 1 ? "person" : "people"}
                  {effectiveSeconds > 0 && ` · ${fmtDuration(effectiveSeconds)}`}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {syncResult && <BatchResultPanel result={syncResult} />}

      <ConfirmSyncModal
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
        onConfirm={handleConfirmSync}
        syncing={syncing}
        error={showConfirm ? error : null}
        targetLabels={targetLabels}
        summary={
          <ul className="mb-3">
            <li>
              <strong>{selectedProjects.length}</strong>{" "}
              {selectedProjects.length === 1 ? "project" : "projects"}
              {projectCodes.length > 0 && (
                <>
                  {" "}
                  ({projectCodes.slice(0, 6).join(", ")}
                  {projectCodes.length > 6 &&
                    ` and ${projectCodes.length - 6} more`}
                  )
                </>
              )}
            </li>
            <li>Period {prettyRange(startDate, endDate)}</li>
            <li>
              <strong>{effectivePeople.length}</strong>{" "}
              {effectivePeople.length === 1 ? "person" : "people"}
              {effectiveSeconds > 0 && (
                <> · {fmtDuration(effectiveSeconds)} of tracked time</>
              )}
            </li>
          </ul>
        }
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Tab 2 — By project: project → period (or all time) → people → sync
// ---------------------------------------------------------------------------

function ByProjectTab() {
  const { projectList } = useContext(ProjectContext);

  const today = new Date();
  const weekAgo = new Date();
  weekAgo.setDate(today.getDate() - 7);

  const [projectId, setProjectId] = useState("");
  const [periodKey, setPeriodKey] = useState("");
  const [customStart, setCustomStart] = useState(fmtDate(weekAgo));
  const [customEnd, setCustomEnd] = useState(fmtDate(today));

  const [breakdown, setBreakdown] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [syncToSonder, setSyncToSonder] = useState(true);
  const [syncToXero, setSyncToXero] = useState(true);

  const [loadingBreakdown, setLoadingBreakdown] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [syncResult, setSyncResult] = useState(null);

  const [showConfirm, setShowConfirm] = useState(false);

  // Only projects that have a Toggl id can be synced. Sorted descending by code,
  // numeric-aware so 20352 sorts above 9999 (and un-coded projects fall last).
  const syncableProjects = useMemo(
    () =>
      (projectList || [])
        .filter((p) => p.togglId)
        .sort((a, b) =>
          (b.code || "").localeCompare(a.code || "", undefined, {
            numeric: true,
          }),
        ),
    [projectList],
  );

  // react-select options for the searchable project picker.
  const projectOptions = useMemo(
    () =>
      syncableProjects.map((p) => ({
        value: p._id,
        label: `${p.code} — ${p.name}`,
      })),
    [syncableProjects],
  );

  const selectedProject = syncableProjects.find((p) => p._id === projectId);
  const periods = useMemo(
    () => buildPeriods(selectedProject, { withAllTime: true }),
    [selectedProject],
  );

  const isAllTime = periodKey === "allTime";
  const activePeriod = periods.find((p) => p.key === periodKey);
  const startDate = periodKey === "custom" ? customStart : activePeriod?.start || "";
  const endDate = periodKey === "custom" ? customEnd : activePeriod?.end || "";
  const rangeChosen = isAllTime || (!!startDate && !!endDate);

  // When the project changes, default to the first sensible period (keeping the
  // current one if it still exists for the new project).
  useEffect(() => {
    if (!projectId) {
      setPeriodKey("");
      return;
    }
    setPeriodKey((prev) => {
      const stillValid = prev === "custom" || periods.some((p) => p.key === prev);
      return stillValid ? prev : periods[0]?.key || "";
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Any change to project or range invalidates a previously loaded preview so a
  // sync can never run against stale data.
  useEffect(() => {
    setBreakdown(null);
    setSyncResult(null);
    setError(null);
  }, [projectId, periodKey, startDate, endDate]);

  // Given the current target checkboxes, who will actually get entries created.
  const personWillSync = (p) =>
    selectedIds.has(p.togglUserId) &&
    ((syncToSonder && p.sonderOk) || (syncToXero && p.xeroOk));

  // Normalize breakdown people to the shared checklist row shape.
  const people = useMemo(
    () =>
      (breakdown?.people || []).map((p) => ({
        ...p,
        sonderOk: p.syncableToSonder,
        xeroOk: p.syncableToXero,
      })),
    [breakdown],
  );
  const effectivePeople = people.filter(personWillSync);
  const effectiveSeconds = effectivePeople.reduce(
    (s, p) => s + p.totalSeconds,
    0,
  );

  const togglePerson = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleLoad = async () => {
    setError(null);
    setSyncResult(null);
    if (!projectId) {
      setError("Select a project first.");
      return;
    }
    if (!rangeChosen) {
      setError("Choose a period.");
      return;
    }
    setLoadingBreakdown(true);
    try {
      const data = await fetchTimeSyncBreakdown(
        isAllTime
          ? { projectId, allTime: true }
          : { projectId, startDate, endDate },
      );
      setBreakdown(data);
      // Pre-select everyone who is syncable to at least one target.
      setSelectedIds(
        new Set(
          (data.people || [])
            .filter((p) => p.syncableToSonder || p.syncableToXero)
            .map((p) => p.togglUserId),
        ),
      );
      // Reflect which targets the project can actually reach.
      setSyncToSonder(data.targets.sonder);
      setSyncToXero(data.targets.xero);
    } catch (err) {
      setError(
        err?.response?.data?.message || err.message || "Failed to load time.",
      );
      setBreakdown(null);
    } finally {
      setLoadingBreakdown(false);
    }
  };

  const locked = breakdown?.locked || null;

  const openConfirm = () => {
    setError(null);
    if (locked) {
      setError(
        `This period is locked (${lockRangeLabel(locked)}) — an admin must unlock it first.`,
      );
      return;
    }
    if (!effectivePeople.length) {
      setError("No syncable people selected.");
      return;
    }
    if (!syncToSonder && !syncToXero) {
      setError("Select at least one target (Sonderplan or Xero).");
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirmSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      const data = await runTimeSync({
        projectId,
        ...(isAllTime ? { allTime: true } : { startDate, endDate }),
        syncToSonder,
        syncToXero,
        // Send exactly the people who will actually sync given the current
        // target selection — matches the count shown in the confirm dialog.
        togglUserIds: effectivePeople.map((p) => p.togglUserId),
      });
      setSyncResult(data);
      setShowConfirm(false);
      // The previewed entries are now synced — clear the stale preview.
      setBreakdown(null);
      setSelectedIds(new Set());
    } catch (err) {
      setError(
        err?.response?.data?.message || err.message || "Time sync failed.",
      );
    } finally {
      setSyncing(false);
    }
  };

  const targetLabels = [syncToSonder && "Sonderplan", syncToXero && "Xero"]
    .filter(Boolean)
    .join(" and ");

  // Label for the loaded range — the all-time breakdown comes back with the
  // resolved floor date, so show it.
  const rangeLabel = isAllTime
    ? breakdown
      ? `all time (since ${prettyRange(breakdown.startDate, breakdown.startDate)})`
      : "all time"
    : prettyRange(startDate, endDate);

  return (
    <>
      <div className="bg-white shadow-md rounded p-5">
        {/* Step 1 — Project */}
        <Form.Group className="mb-4">
          <Form.Label className="font-semibold">
            <span className="text-gray-400 mr-2">1</span>Project
          </Form.Label>
          <Select
            inputId="project-select"
            classNamePrefix="react-select"
            options={projectOptions}
            value={projectOptions.find((o) => o.value === projectId) || null}
            onChange={(opt) => setProjectId(opt ? opt.value : "")}
            placeholder="Select a project…"
            isClearable
            isSearchable
            noOptionsMessage={() => "No matching projects"}
          />
          {selectedProject && (
            <Form.Text className="text-muted">
              {selectedProject.lastTimeSyncDate
                ? `Last synced ${prettyRange(
                    fmtDate(new Date(selectedProject.lastTimeSyncDate)),
                    fmtDate(new Date(selectedProject.lastTimeSyncDate)),
                  )}${
                    selectedProject.lastTimeSyncBy
                      ? ` by ${selectedProject.lastTimeSyncBy}`
                      : ""
                  }`
                : "Never synced."}
            </Form.Text>
          )}
        </Form.Group>

        {/* Step 2 — Period */}
        <Form.Group className="mb-4">
          <Form.Label className="font-semibold">
            <span className="text-gray-400 mr-2">2</span>Period
          </Form.Label>
          <PeriodButtons
            periods={periods}
            periodKey={periodKey}
            setPeriodKey={setPeriodKey}
            customStart={customStart}
            setCustomStart={setCustomStart}
            customEnd={customEnd}
            setCustomEnd={setCustomEnd}
            disabled={!projectId}
          />
          {isAllTime ? (
            <Form.Text className="text-muted d-block mt-2">
              Pulling the project’s entire history — this can take a while for
              long-running projects.
            </Form.Text>
          ) : (
            startDate &&
            endDate && (
              <Form.Text className="text-muted d-block mt-2">
                Pulling {prettyRange(startDate, endDate)}
              </Form.Text>
            )
          )}
        </Form.Group>

        <Button
          variant="primary"
          onClick={handleLoad}
          disabled={loadingBreakdown || !projectId || !rangeChosen}
        >
          {loadingBreakdown ? (
            <>
              <Spinner size="sm" animation="border" className="me-2" />
              Loading…
            </>
          ) : breakdown ? (
            "Reload time"
          ) : (
            "Load time"
          )}
        </Button>
      </div>

      {error && !showConfirm && (
        <Alert variant="danger" className="mt-4">
          {error}
        </Alert>
      )}

      {/* Step 3 — People + targets */}
      {breakdown && (
        <div className="mt-4 bg-white shadow-md rounded p-5">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-semibold mb-0">
              <span className="text-gray-400 mr-2">3</span>People who tracked time
            </h3>
            <span className="text-sm text-gray-600">
              {breakdown.entryCount} entries · {breakdown.formattedDuration} total
            </span>
          </div>

          <LockedBanner locked={locked} />

          {people.length === 0 ? (
            <Alert variant="info" className="mt-3 mb-0">
              No time tracked on this project for {rangeLabel}.
            </Alert>
          ) : (
            <>
              <PeopleChecklist
                people={people}
                selectedIds={selectedIds}
                onTogglePerson={togglePerson}
                onToggleAll={(ids) => setSelectedIds(new Set(ids))}
                syncToSonder={syncToSonder}
                syncToXero={syncToXero}
              />

              <hr className="my-4" />

              <div className="flex flex-wrap gap-6 items-center">
                <Form.Check
                  type="checkbox"
                  id="sync-sonder"
                  label="Sonderplan"
                  checked={syncToSonder}
                  disabled={!breakdown.targets.sonder}
                  onChange={(e) => setSyncToSonder(e.target.checked)}
                />
                <Form.Check
                  type="checkbox"
                  id="sync-xero"
                  label="Xero"
                  checked={syncToXero}
                  disabled={!breakdown.targets.xero}
                  onChange={(e) => setSyncToXero(e.target.checked)}
                />
                {(!breakdown.targets.sonder || !breakdown.targets.xero) && (
                  <span className="text-sm text-muted">
                    {!breakdown.targets.sonder && "No Sonder id"}
                    {!breakdown.targets.sonder && !breakdown.targets.xero && " · "}
                    {!breakdown.targets.xero &&
                      (breakdown.xeroClosed
                        ? "Xero project closed"
                        : "No Xero id/task")}
                    {" — that target is unavailable for this project."}
                  </span>
                )}
              </div>

              {!locked && (
                <Alert variant="warning" className="mt-4 mb-3">
                  <strong>Live sync.</strong> Running this creates real entries
                  in {targetLabels || "the selected targets"} — you’ll confirm
                  before anything is written.
                </Alert>
              )}
              <div className={locked ? "mt-4" : ""}>
                <Button variant="danger" onClick={openConfirm} disabled={!!locked}>
                  Sync {effectivePeople.length}{" "}
                  {effectivePeople.length === 1 ? "person" : "people"}
                  {effectiveSeconds > 0 && ` · ${fmtDuration(effectiveSeconds)}`}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {syncResult && <ResultPanel result={syncResult} />}

      <ConfirmSyncModal
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
        onConfirm={handleConfirmSync}
        syncing={syncing}
        error={showConfirm ? error : null}
        targetLabels={targetLabels}
        summary={
          <ul className="mb-3">
            <li>
              Project <strong>{selectedProject?.code}</strong> —{" "}
              {selectedProject?.name}
            </li>
            <li>
              {isAllTime ? (
                <>
                  Period <strong>all time</strong> (the project’s entire history)
                </>
              ) : (
                <>Period {prettyRange(startDate, endDate)}</>
              )}
            </li>
            <li>
              <strong>{effectivePeople.length}</strong>{" "}
              {effectivePeople.length === 1 ? "person" : "people"}
              {effectiveSeconds > 0 && (
                <> · {fmtDuration(effectiveSeconds)} of tracked time</>
              )}
            </li>
          </ul>
        }
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Tab 3 — Locked periods: view for everyone, lock/unlock for admins only
// ---------------------------------------------------------------------------

function LocksTab({ isAdmin }) {
  const [locks, setLocks] = useState(null); // null = loading
  const [error, setError] = useState(null);

  const [newStart, setNewStart] = useState("");
  const [newEnd, setNewEnd] = useState("");
  const [newReason, setNewReason] = useState("");
  const [saving, setSaving] = useState(false);

  const [unlockTarget, setUnlockTarget] = useState(null); // lock pending confirmation
  const [unlocking, setUnlocking] = useState(false);

  const load = async () => {
    try {
      setError(null);
      const data = await fetchTimeSyncLocks({ includeInactive: true });
      setLocks(data);
    } catch (err) {
      setError(
        err?.response?.data?.message || err.message || "Failed to load locks.",
      );
      setLocks([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeLocks = (locks || []).filter((l) => l.active);
  const historyLocks = (locks || []).filter((l) => !l.active);

  const fmtWhen = (d) => (d ? new Date(d).toLocaleDateString() : "");

  const handleCreate = async () => {
    setError(null);
    if (!newStart && !newEnd) {
      setError("Set at least one of the two dates.");
      return;
    }
    if (newStart && newEnd && newStart > newEnd) {
      setError("Start date must not be after end date.");
      return;
    }
    setSaving(true);
    try {
      await createTimeSyncLock({
        startDate: newStart,
        endDate: newEnd,
        reason: newReason.trim(),
      });
      setNewStart("");
      setNewEnd("");
      setNewReason("");
      await load();
    } catch (err) {
      setError(
        err?.response?.data?.message || err.message || "Failed to create lock.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleUnlock = async () => {
    if (!unlockTarget) return;
    setUnlocking(true);
    setError(null);
    try {
      await unlockTimeSyncLock(unlockTarget._id);
      setUnlockTarget(null);
      await load();
    } catch (err) {
      setError(
        err?.response?.data?.message || err.message || "Failed to unlock.",
      );
    } finally {
      setUnlocking(false);
    }
  };

  return (
    <>
      <div className="bg-white shadow-md rounded p-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-semibold mb-0">Locked periods</h3>
          {locks === null && <Spinner size="sm" animation="border" />}
        </div>
        <p className="text-sm text-gray-600 mb-3">
          No live sync can write into a locked period — lock a month once it has
          been reconciled and invoiced. Only admin accounts can lock or unlock.
        </p>

        {error && <Alert variant="danger">{error}</Alert>}

        {locks !== null && activeLocks.length === 0 ? (
          <Alert variant="info" className="mb-0">
            No periods are currently locked.
          </Alert>
        ) : (
          <div className="border rounded divide-y">
            {activeLocks.map((l) => (
              <div key={l._id} className="flex items-center gap-3 px-3 py-2">
                <span className="flex-1">
                  <span className="font-medium">{lockRangeLabel(l)}</span>
                  {l.reason && (
                    <span className="text-gray-600 text-sm"> — {l.reason}</span>
                  )}
                  <div className="text-xs text-gray-500">
                    Locked by {l.lockedByEmail || "unknown"} on{" "}
                    {fmtWhen(l.lockedAt)}
                  </div>
                </span>
                <Badge bg="danger">locked</Badge>
                {isAdmin && (
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => setUnlockTarget(l)}
                  >
                    Unlock…
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        {!isAdmin && (
          <p className="text-sm text-muted mt-3 mb-0">
            You can view locks, but only an admin can add or remove them.
          </p>
        )}
      </div>

      {isAdmin && (
        <div className="mt-4 bg-white shadow-md rounded p-5">
          <h3 className="font-semibold mb-1">Lock a period</h3>
          <p className="text-sm text-gray-600 mb-3">
            Leave one date blank for an open-ended lock — e.g. only an end date
            locks <em>everything up to and including</em> that date.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Form.Label className="text-sm text-muted">Start date</Form.Label>
              <Form.Control
                type="date"
                value={newStart}
                onChange={(e) => setNewStart(e.target.value)}
              />
            </div>
            <div>
              <Form.Label className="text-sm text-muted">End date</Form.Label>
              <Form.Control
                type="date"
                value={newEnd}
                onChange={(e) => setNewEnd(e.target.value)}
              />
            </div>
          </div>
          <div className="mt-3">
            <Form.Label className="text-sm text-muted">
              Reason (shown to anyone whose sync is blocked)
            </Form.Label>
            <Form.Control
              value={newReason}
              placeholder="e.g. July closed off — invoiced"
              onChange={(e) => setNewReason(e.target.value)}
            />
          </div>
          <Button
            variant="danger"
            className="mt-3"
            onClick={handleCreate}
            disabled={saving || (!newStart && !newEnd)}
          >
            {saving ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Locking…
              </>
            ) : (
              "Lock period"
            )}
          </Button>
        </div>
      )}

      {historyLocks.length > 0 && (
        <details className="mt-4">
          <summary className="text-sm text-gray-600 cursor-pointer">
            {historyLocks.length} previously unlocked{" "}
            {historyLocks.length === 1 ? "period" : "periods"}
          </summary>
          <div className="border rounded divide-y mt-2 bg-white">
            {historyLocks.map((l) => (
              <div key={l._id} className="px-3 py-2 text-sm text-gray-600">
                <span className="font-medium">{lockRangeLabel(l)}</span>
                {l.reason && <> — {l.reason}</>}
                <div className="text-xs text-gray-500">
                  Locked by {l.lockedByEmail || "unknown"} on{" "}
                  {fmtWhen(l.lockedAt)} · unlocked by{" "}
                  {l.unlockedByEmail || "unknown"} on {fmtWhen(l.unlockedAt)}
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      <Modal
        show={!!unlockTarget}
        onHide={() => !unlocking && setUnlockTarget(null)}
        centered
      >
        <Modal.Header closeButton={!unlocking}>
          <Modal.Title>Unlock period</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            Unlock <strong>{unlockTarget && lockRangeLabel(unlockTarget)}</strong>
            {unlockTarget?.reason && <> ({unlockTarget.reason})</>}? Live syncs
            will be able to write into this period again.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setUnlockTarget(null)}
            disabled={unlocking}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleUnlock} disabled={unlocking}>
            {unlocking ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Unlocking…
              </>
            ) : (
              "Unlock"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------

function TimeSync() {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState("byPeriod");

  const isAdmin = currentUser?.role === "admin";
  const canSync = currentUser && (isAdmin || currentUser.canSyncTime);

  if (!currentUser) return null;

  if (!canSync) {
    return (
      <div className="pt-24 px-6 max-w-3xl mx-auto">
        <Alert variant="warning">
          You don’t have access to the time-sync controls. Ask an admin to
          enable them for your account.
        </Alert>
      </div>
    );
  }

  return (
    <div className="pt-24 px-6 pb-10 max-w-3xl mx-auto">
      <h2 className="text-2xl font-bold mb-1">Time Sync</h2>
      <p className="text-gray-600 mb-4">
        Push tracked time from Toggl into Sonderplan and Xero. Start from a
        period to see every project worked on in it, or start from a single
        project.
      </p>

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k || "byPeriod")}
        className="mb-4"
      >
        <Tab eventKey="byPeriod" title="By period">
          <ByPeriodTab />
        </Tab>
        <Tab eventKey="byProject" title="By project">
          <ByProjectTab />
        </Tab>
        <Tab eventKey="locks" title="Locked periods">
          <LocksTab isAdmin={isAdmin} />
        </Tab>
      </Tabs>
    </div>
  );
}

export default TimeSync;
